import { useLingui } from '@lingui/react/macro';
import { useCallback } from 'react';
import { SingleValue } from 'react-select';

import { useOrganizer } from 'modules/organizer/ui/hooks/use-organizer';
import { useLazyListPlayersQuery } from 'modules/players/store/players-api';
import { getFullName } from 'shared/utils';
import { UDAsyncSelect } from 'ud-form/components/select/component';
import { UDText } from 'ud-ui/components';
import { PlayerSelectOption, playerToPlayerOption } from './player-option';

/** Выдача поиска игроков — длиннее командной, ФИО чаще совпадают. */
const SEARCH_LIMIT = 30;

const selectComponents = {
  SingleValue: (props: any) => (
    <div>
      <UDText type="subhead">{getFullName(props.data?.value)}</UDText>
    </div>
  ),
};

interface PlayerAsyncSelectProps {
  value: PlayerSelectOption | null;
  onChange: (option: SingleValue<PlayerSelectOption>) => void;
  autoFocus?: boolean;
  className?: string;
  placeholder?: string;
}

/**
 * Поиск игрока текущей организации по ФИО.
 *
 * Организацию берёт сам — вызывающей стороне остаётся принять выбранного игрока.
 * Используется там, где игрока надо к чему-то привязать: сейчас это добавление в состав
 * команды.
 */
export function PlayerAsyncSelect({ value, onChange, autoFocus, className, placeholder }: PlayerAsyncSelectProps) {
  const { t } = useLingui();
  const { info } = useOrganizer();
  const tournamentId = info?.id;
  const [searchPlayers] = useLazyListPlayersQuery();

  const handleLoadOptions = useCallback(
    async (inputValue: string) => {
      if (!tournamentId) return [];

      // preferCacheValue: повтор того же ввода между открытиями модалки в сеть не идёт.
      const players = await searchPlayers({ tournamentId, limit: SEARCH_LIMIT, offset: 0, nameFilter: inputValue }, true).unwrap();

      return players.map(playerToPlayerOption);
    },
    [tournamentId, searchPlayers],
  );

  return (
    <UDAsyncSelect<PlayerSelectOption>
      autoFocus={autoFocus}
      className={className}
      placeholder={placeholder ?? t`Введите ФИО игрока`}
      components={selectComponents}
      loadOptions={handleLoadOptions}
      value={value}
      onChange={onChange}
    />
  );
}
